import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, X, Check, Database, Upload, Download, FileText, Save, Loader2, Smartphone, Image as ImageIcon, Shield, Search } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { masterDataApi, modelApi, accessoryApi, invoiceTermsApi, estimationTermsApi } from '@/services/masterDataApi';
import { companyApi } from '@/services/companyApi';
import { getCurrencySymbol } from '@/utils/currency';
import { compressImageFile } from '@/utils/imageCompression';
import TablePagination from '@/components/common/TablePagination';
import { useDebounce } from '@/hooks/useDebounce';
import SimpleSelect from '@/components/common/SimpleSelect';

const FAULT_LEVEL_OPTIONS = [
  { value: '1', label: 'Level 1 - Easy' },
  { value: '2', label: 'Level 2 - Simple' },
  { value: '3', label: 'Level 3 - Medium' },
  { value: '4', label: 'Level 4 - Complex' },
  { value: '5', label: 'Level 5 - Hard' },
];
import {
  ItemCategory,
  ItemUnit,
  ItemGSTRate,
  ItemBrand,
  ItemModel,
  Fault,
  PaymentMethod,
  ExpenseCategory,
  ServiceIssue,
  Accessory,
  InvoiceTerms,
  EstimationTerms,
  CreateItemCategoryDto,
  CreateItemUnitDto,
  CreateItemGSTRateDto,
  CreateItemBrandDto,
  CreateItemModelDto,
  CreateFaultDto,
  CreatePaymentMethodDto,
  CreateExpenseCategoryDto,
  CreateServiceIssueDto,
  CreateAccessoryDto,
  CreateInvoiceTermsDto,
  CreateEstimationTermsDto,
} from '@/types/masters';

type MasterType = 'category' | 'unit' | 'gst-rate' | 'brand' | 'model' | 'fault' | 'payment-method' | 'expense-category' | 'service-issue' | 'accessory' | 'invoice-terms' | 'estimation-terms' | 'jobsheet-instructions';

export default function MastersPage() {
  const [activeTab, setActiveTab] = useState<MasterType>('category');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const queryClient = useQueryClient();

  // Server-side pagination
  const ITEMS_PER_PAGE = 20;
  const [pageState, setPageState] = useState<Record<string, number>>({});
  const [searchState, setSearchState] = useState<Record<string, string>>({});
  const [debouncedSearchMap, setDebouncedSearchMap] = useState<Record<string, string>>({});
  const [modelBrandFilter, setModelBrandFilter] = useState('');

  // Debounce active tab search
  const activeSearch = searchState[activeTab] || '';
  const debouncedActiveSearch = useDebounce(activeSearch, 300);

  // Persist debounced search per tab so queryKeys stay stable when switching tabs
  useEffect(() => {
    setDebouncedSearchMap(prev => ({ ...prev, [activeTab]: debouncedActiveSearch }));
  }, [debouncedActiveSearch, activeTab]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setPageState(prev => ({ ...prev, [activeTab]: 1 }));
  }, [debouncedActiveSearch, activeTab]);

  // Reset model brand filter page
  useEffect(() => {
    setPageState(prev => ({ ...prev, model: 1 }));
  }, [modelBrandFilter]);

  const getPage = (tab: string) => pageState[tab] || 1;
  const getSearch = (tab: string) => {
    if (tab === activeTab) return debouncedActiveSearch;
    return debouncedSearchMap[tab] || '';
  };
  const setPage = (tab: string, page: number) => setPageState(prev => ({ ...prev, [tab]: page }));
  const setSearch = (tab: string, search: string) => setSearchState(prev => ({ ...prev, [tab]: search }));

  // Master data queries with caching (data rarely changes)
  const masterDataStaleTime = 10 * 60 * 1000; // 10 minutes

  const categoriesQuery = useQuery({
    queryKey: ['categories', getPage('category'), getSearch('category')],
    queryFn: () => masterDataApi.categories.getAll({ page: getPage('category'), limit: ITEMS_PER_PAGE, search: getSearch('category') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const unitsQuery = useQuery({
    queryKey: ['units', getPage('unit'), getSearch('unit')],
    queryFn: () => masterDataApi.units.getAll({ page: getPage('unit'), limit: ITEMS_PER_PAGE, search: getSearch('unit') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const gstRatesQuery = useQuery({
    queryKey: ['gstRates', getPage('gst-rate'), getSearch('gst-rate')],
    queryFn: () => masterDataApi.gstRates.getAll({ page: getPage('gst-rate'), limit: ITEMS_PER_PAGE, search: getSearch('gst-rate') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const brandsQuery = useQuery({
    queryKey: ['brands', getPage('brand'), getSearch('brand')],
    queryFn: () => masterDataApi.brands.getAll({ page: getPage('brand'), limit: ITEMS_PER_PAGE, search: getSearch('brand') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  // Separate query for brand dropdown (all brands, no pagination)
  const allBrandsQuery = useQuery({
    queryKey: ['brands-all'],
    queryFn: () => masterDataApi.brands.getAll({ limit: 500, isActive: true }),
    staleTime: masterDataStaleTime,
  });

  const modelsQuery = useQuery({
    queryKey: ['models', getPage('model'), getSearch('model'), modelBrandFilter],
    queryFn: () => masterDataApi.models.getAll({ page: getPage('model'), limit: ITEMS_PER_PAGE, search: getSearch('model') || undefined, brandId: modelBrandFilter || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const faultsQuery = useQuery({
    queryKey: ['faults', getPage('fault'), getSearch('fault')],
    queryFn: () => masterDataApi.faults.getAll({ page: getPage('fault'), limit: ITEMS_PER_PAGE, search: getSearch('fault') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ['paymentMethods', getPage('payment-method'), getSearch('payment-method')],
    queryFn: () => masterDataApi.paymentMethods.getAll({ page: getPage('payment-method'), limit: ITEMS_PER_PAGE, search: getSearch('payment-method') || undefined, isActive: true }),
    staleTime: 30 * 60 * 1000, // 30 minutes - rarely changes
    placeholderData: (prev: any) => prev,
  });

  const expenseCategoriesQuery = useQuery({
    queryKey: ['expenseCategories', getPage('expense-category'), getSearch('expense-category')],
    queryFn: () => masterDataApi.expenseCategories.getAll({ page: getPage('expense-category'), limit: ITEMS_PER_PAGE, search: getSearch('expense-category') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const serviceIssuesQuery = useQuery({
    queryKey: ['serviceIssues', getPage('service-issue'), getSearch('service-issue')],
    queryFn: () => masterDataApi.serviceIssues.getAll({ page: getPage('service-issue'), limit: ITEMS_PER_PAGE, search: getSearch('service-issue') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const accessoriesQuery = useQuery({
    queryKey: ['accessories', getPage('accessory'), getSearch('accessory')],
    queryFn: () => accessoryApi.getAll({ page: getPage('accessory'), limit: ITEMS_PER_PAGE, search: getSearch('accessory') || undefined, isActive: true }),
    staleTime: masterDataStaleTime,
    placeholderData: (prev: any) => prev,
  });

  const invoiceTermsQuery = useQuery({
    queryKey: ['invoiceTerms'],
    queryFn: () => invoiceTermsApi.getAll({ limit: 100, isActive: true }),
    staleTime: masterDataStaleTime,
  });

  const estimationTermsQuery = useQuery({
    queryKey: ['estimationTerms'],
    queryFn: () => estimationTermsApi.getAll({ limit: 100, isActive: true }),
    staleTime: masterDataStaleTime,
  });

  // Invalidate all pages of a master query when data changes (create/update/delete)
  const invalidateMaster = (queryKey: string) => {
    queryClient.invalidateQueries({ queryKey: [queryKey] });
    if (queryKey === 'brands') {
      queryClient.invalidateQueries({ queryKey: ['brands-all'] });
    }
  };

  // Company query for job sheet instructions
  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.getCompany,
    staleTime: masterDataStaleTime,
  });

  // State for job sheet instructions
  const [jobSheetInstructions, setJobSheetInstructions] = useState('');
  const [instructionsChanged, setInstructionsChanged] = useState(false);

  // State for invoice terms text
  const [invoiceTermsText, setInvoiceTermsText] = useState('');
  const [invoiceTermsChanged, setInvoiceTermsChanged] = useState(false);

  // State for estimation terms text
  const [estimationTermsText, setEstimationTermsText] = useState('');
  const [estimationTermsChanged, setEstimationTermsChanged] = useState(false);

  // Update local state when company data loads
  useEffect(() => {
    if (companyQuery.data?.jobSheetInstructions) {
      setJobSheetInstructions(companyQuery.data.jobSheetInstructions);
    }
    if (companyQuery.data?.invoiceTermsText) {
      setInvoiceTermsText(companyQuery.data.invoiceTermsText);
    }
    if (companyQuery.data?.estimationTermsText) {
      setEstimationTermsText(companyQuery.data.estimationTermsText);
    }
  }, [companyQuery.data]);

  // Mutation for updating job sheet instructions
  const updateInstructionsMutation = useMutation({
    mutationFn: (instructions: string) => companyApi.updateCompany({ jobSheetInstructions: instructions }),
    onSuccess: () => {
      toast.success('Job sheet instructions saved successfully');
      queryClient.invalidateQueries({ queryKey: ['company'] });
      setInstructionsChanged(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save instructions');
    },
  });

  // Mutation for updating invoice terms
  const updateInvoiceTermsMutation = useMutation({
    mutationFn: (terms: string) => companyApi.updateCompany({ invoiceTermsText: terms }),
    onSuccess: () => {
      toast.success('Invoice terms saved successfully');
      queryClient.invalidateQueries({ queryKey: ['company'] });
      setInvoiceTermsChanged(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save invoice terms');
    },
  });

  // Mutation for updating estimation terms
  const updateEstimationTermsMutation = useMutation({
    mutationFn: (terms: string) => companyApi.updateCompany({ estimationTermsText: terms }),
    onSuccess: () => {
      toast.success('Estimation terms saved successfully');
      queryClient.invalidateQueries({ queryKey: ['company'] });
      setEstimationTermsChanged(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save estimation terms');
    },
  });

  const handleSaveInstructions = () => {
    updateInstructionsMutation.mutate(jobSheetInstructions);
  };

  const handleSaveInvoiceTerms = () => {
    updateInvoiceTermsMutation.mutate(invoiceTermsText);
  };

  const handleSaveEstimationTerms = () => {
    updateEstimationTermsMutation.mutate(estimationTermsText);
  };

  const handleOpenModal = (item?: any) => {
    setEditingItem(item || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  // Tab config for mobile tab bar
  const tabItems: { key: MasterType; label: string; count?: number }[] = [
    { key: 'category', label: 'Categories', count: categoriesQuery.data?.pagination?.total || 0 },
    { key: 'unit', label: 'Units', count: unitsQuery.data?.pagination?.total || 0 },
    { key: 'gst-rate', label: 'GST Rates', count: gstRatesQuery.data?.pagination?.total || 0 },
    { key: 'brand', label: 'Brands', count: brandsQuery.data?.pagination?.total || 0 },
    { key: 'model', label: 'Models', count: modelsQuery.data?.pagination?.total || 0 },
    { key: 'fault', label: 'Faults', count: faultsQuery.data?.pagination?.total || 0 },
    { key: 'payment-method', label: 'Payment', count: paymentMethodsQuery.data?.pagination?.total || 0 },
    { key: 'expense-category', label: 'Expenses', count: expenseCategoriesQuery.data?.pagination?.total || 0 },
    { key: 'service-issue', label: 'Damage', count: serviceIssuesQuery.data?.pagination?.total || 0 },
    { key: 'accessory', label: 'Accessories', count: accessoriesQuery.data?.pagination?.total || 0 },
    { key: 'invoice-terms', label: 'Invoice Terms', count: invoiceTermsQuery.data?.data.length || 0 },
    { key: 'estimation-terms', label: 'Estimation Terms', count: estimationTermsQuery.data?.data.length || 0 },
    { key: 'jobsheet-instructions', label: 'Job Sheet' },
  ];

  return (
    <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
      {/* Mobile Tab Bar */}
      <div className="md:hidden bg-white border-b border-gray-200 overflow-x-auto">
        <div className="flex gap-1.5 p-2 min-w-max">
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === tab.key
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Master Data Sidebar (Desktop) */}
      <div className="hidden md:block w-56 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <Database className="h-5 w-5 text-purple-600" />
              <h2 className="text-sm font-bold text-gray-900">Master Data</h2>
            </div>
            <p className="text-xs text-gray-500">
              Manage inventory master data
            </p>
          </div>

          <nav className="p-3 space-y-1">
            <button
              onClick={() => setActiveTab('category')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'category'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Item Categories</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'category'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {categoriesQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('unit')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'unit'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Item Units</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'unit'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {unitsQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('gst-rate')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'gst-rate'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>GST Rates</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'gst-rate'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {gstRatesQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('brand')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'brand'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Item Brands</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'brand'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {brandsQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('model')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'model'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Item Models</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'model'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {modelsQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('fault')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'fault'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Faults</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'fault'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {faultsQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('payment-method')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'payment-method'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Payment Methods</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'payment-method'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {paymentMethodsQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('expense-category')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'expense-category'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Expense Categories</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'expense-category'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {expenseCategoriesQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('service-issue')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'service-issue'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Damage Conditions</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'service-issue'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {serviceIssuesQuery.data?.pagination?.total || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('accessory')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'accessory'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Accessories</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'accessory'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {accessoriesQuery.data?.pagination?.total || 0}
              </span>
            </button>

            {/* Divider */}
            <div className="my-2 border-t border-gray-200"></div>

            <button
              onClick={() => setActiveTab('invoice-terms')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'invoice-terms'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Invoice Terms</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'invoice-terms'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {invoiceTermsQuery.data?.data.length || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('estimation-terms')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'estimation-terms'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Estimation Terms</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'estimation-terms'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {estimationTermsQuery.data?.data.length || 0}
              </span>
            </button>

            {/* Divider */}
            <div className="my-2 border-t border-gray-200"></div>

            <button
              onClick={() => setActiveTab('jobsheet-instructions')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'jobsheet-instructions'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-4 w-4" />
              <span>Job Sheet Instructions</span>
            </button>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-auto">
          <div className="p-4">
            {/* Content Sections */}
            {activeTab === 'category' && (
              <CategorySection
                data={categoriesQuery.data?.data || []}
                isLoading={categoriesQuery.isLoading}
                pagination={categoriesQuery.data?.pagination}
                search={searchState['category'] || ''}
                onSearchChange={(v) => setSearch('category', v)}
                onPageChange={(p) => setPage('category', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'unit' && (
              <UnitSection
                data={unitsQuery.data?.data || []}
                isLoading={unitsQuery.isLoading}
                pagination={unitsQuery.data?.pagination}
                search={searchState['unit'] || ''}
                onSearchChange={(v) => setSearch('unit', v)}
                onPageChange={(p) => setPage('unit', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
              />
            )}
            {activeTab === 'gst-rate' && (
              <GSTRateSection
                data={gstRatesQuery.data?.data || []}
                isLoading={gstRatesQuery.isLoading}
                pagination={gstRatesQuery.data?.pagination}
                search={searchState['gst-rate'] || ''}
                onSearchChange={(v) => setSearch('gst-rate', v)}
                onPageChange={(p) => setPage('gst-rate', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
              />
            )}
            {activeTab === 'brand' && (
              <BrandSection
                data={brandsQuery.data?.data || []}
                isLoading={brandsQuery.isLoading}
                pagination={brandsQuery.data?.pagination}
                search={searchState['brand'] || ''}
                onSearchChange={(v) => setSearch('brand', v)}
                onPageChange={(p) => setPage('brand', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'model' && (
              <ModelSection
                data={modelsQuery.data?.data || []}
                isLoading={modelsQuery.isLoading}
                pagination={modelsQuery.data?.pagination}
                search={searchState['model'] || ''}
                onSearchChange={(v) => setSearch('model', v)}
                onPageChange={(p) => setPage('model', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
                brands={allBrandsQuery.data?.data || []}
                brandFilter={modelBrandFilter}
                onBrandFilterChange={setModelBrandFilter}
              />
            )}
            {activeTab === 'fault' && (
              <FaultSection
                data={faultsQuery.data?.data || []}
                isLoading={faultsQuery.isLoading}
                pagination={faultsQuery.data?.pagination}
                search={searchState['fault'] || ''}
                onSearchChange={(v) => setSearch('fault', v)}
                onPageChange={(p) => setPage('fault', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
              />
            )}
            {activeTab === 'payment-method' && (
              <PaymentMethodSection
                data={paymentMethodsQuery.data?.data || []}
                isLoading={paymentMethodsQuery.isLoading}
                pagination={paymentMethodsQuery.data?.pagination}
                search={searchState['payment-method'] || ''}
                onSearchChange={(v) => setSearch('payment-method', v)}
                onPageChange={(p) => setPage('payment-method', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'expense-category' && (
              <ExpenseCategorySection
                data={expenseCategoriesQuery.data?.data || []}
                isLoading={expenseCategoriesQuery.isLoading}
                pagination={expenseCategoriesQuery.data?.pagination}
                search={searchState['expense-category'] || ''}
                onSearchChange={(v) => setSearch('expense-category', v)}
                onPageChange={(p) => setPage('expense-category', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'service-issue' && (
              <ServiceIssueSection
                data={serviceIssuesQuery.data?.data || []}
                isLoading={serviceIssuesQuery.isLoading}
                pagination={serviceIssuesQuery.data?.pagination}
                search={searchState['service-issue'] || ''}
                onSearchChange={(v) => setSearch('service-issue', v)}
                onPageChange={(p) => setPage('service-issue', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'accessory' && (
              <AccessorySection
                data={accessoriesQuery.data?.data || []}
                isLoading={accessoriesQuery.isLoading}
                pagination={accessoriesQuery.data?.pagination}
                search={searchState['accessory'] || ''}
                onSearchChange={(v) => setSearch('accessory', v)}
                onPageChange={(p) => setPage('accessory', p)}
                onAdd={() => handleOpenModal()}
                onEdit={handleOpenModal}
                onImport={() => setIsImportModalOpen(true)}
              />
            )}
            {activeTab === 'invoice-terms' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Invoice Terms & Conditions
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      These terms will appear on all invoices. Supports any language (Tamil, Hindi, English, etc.)
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  {companyQuery.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Invoice Terms & Conditions
                        </label>
                        <textarea
                          value={invoiceTermsText}
                          onChange={(e) => {
                            setInvoiceTermsText(e.target.value);
                            setInvoiceTermsChanged(true);
                          }}
                          rows={15}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-normal text-sm"
                          placeholder="Enter invoice terms & conditions here...

Example:
1. பில் பெற்றவுடன் பணம் செலுத்த வேண்டும்
2. பொருட்கள் விற்பனைக்குப் பிறகு திருப்பி அளிக்கப்படாது

1. Payment due upon receipt
2. Goods once sold cannot be returned
3. Subject to local jurisdiction"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Tip: You can write in any language. The text will appear exactly as typed on the invoice PDF.
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleSaveInvoiceTerms}
                          disabled={!invoiceTermsChanged || updateInvoiceTermsMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {updateInvoiceTermsMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save Invoice Terms
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'estimation-terms' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Estimation Terms & Conditions
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      These terms will appear on all estimations. Supports any language (Tamil, Hindi, English, etc.)
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  {companyQuery.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estimation Terms & Conditions
                        </label>
                        <textarea
                          value={estimationTermsText}
                          onChange={(e) => {
                            setEstimationTermsText(e.target.value);
                            setEstimationTermsChanged(true);
                          }}
                          rows={15}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-normal text-sm"
                          placeholder="Enter estimation terms & conditions here...

Example:
1. மதிப்பீடு 7 நாட்களுக்கு மட்டுமே செல்லுபடியாகும்
2. உண்மையான விலை மாறுபடலாம்

1. Estimate valid for 7 days only
2. Actual price may vary based on inspection
3. Advance payment required to start work"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Tip: You can write in any language. The text will appear exactly as typed on the estimation PDF.
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleSaveEstimationTerms}
                          disabled={!estimationTermsChanged || updateEstimationTermsMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {updateEstimationTermsMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save Estimation Terms
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'jobsheet-instructions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Job Sheet Instructions
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      These instructions will appear on all job sheets. Supports any language (Tamil, Hindi, English, etc.)
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  {companyQuery.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Instructions / Terms & Conditions
                        </label>
                        <textarea
                          value={jobSheetInstructions}
                          onChange={(e) => {
                            setJobSheetInstructions(e.target.value);
                            setInstructionsChanged(true);
                          }}
                          rows={15}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-normal text-sm"
                          placeholder="Enter job sheet instructions here...

Example:
1. அட்வான்ஸ் பேமென்ட் கட்டாயம்
2. 30 நாட்கள் வாரண்டி உள்ளது
3. சாதனத்தை 30 நாட்களுக்குள் எடுக்க வேண்டும்

1. Advance payment is mandatory
2. 30 days warranty on repairs
3. Device must be collected within 30 days"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Tip: You can write in any language. The text will appear exactly as typed on the job sheet PDF.
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleSaveInstructions}
                          disabled={!instructionsChanged || updateInstructionsMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {updateInstructionsMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save Instructions
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <MasterDataModal
            type={activeTab}
            item={editingItem}
            onClose={handleCloseModal}
          />
        )}

        {/* Import Modal */}
        {isImportModalOpen && (
          <ImportModal
            type={activeTab}
            onClose={() => setIsImportModalOpen(false)}
            onSuccess={() => {
              setIsImportModalOpen(false);
              // Invalidate the appropriate query
              const qk = activeTab === 'category' ? 'categories' :
                activeTab === 'unit' ? 'units' :
                activeTab === 'gst-rate' ? 'gstRates' :
                activeTab === 'brand' ? 'brands' :
                activeTab === 'model' ? 'models' :
                activeTab === 'fault' ? 'faults' :
                activeTab === 'payment-method' ? 'paymentMethods' :
                activeTab === 'expense-category' ? 'expenseCategories' :
                activeTab === 'service-issue' ? 'serviceIssues' :
                'accessories';
              invalidateMaster(qk);
            }}
          />
        )}
      </div>
  );
}

// Shared pagination props for section components
interface SectionPaginationProps {
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  search: string;
  onSearchChange: (search: string) => void;
  onPageChange: (page: number) => void;
}

// Search bar component used in each section header
function SectionSearchBar({ search, onSearchChange, placeholder }: { search: string; onSearchChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative flex-1 max-w-sm">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs"
      />
    </div>
  );
}

// Category Section Component
function CategorySection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: ItemCategory[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ItemCategory) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.categories.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Category deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Item Categories</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Category
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search categories..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((category) => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {category.name}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {category.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      category.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(category)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this category?')) {
                        deleteMutation.mutate(category.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No categories found. Click "Add Category" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Unit Section Component
function UnitSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
}: {
  data: ItemUnit[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ItemUnit) => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.units.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      toast.success('Unit deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete unit');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Item Units</h2>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
          >
            <Plus className="h-4 w-4" />
            Add Unit
          </button>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search units..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Symbol
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((unit) => (
              <tr key={unit.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {unit.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {unit.symbol || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {unit.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      unit.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {unit.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(unit)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this unit?')) {
                        deleteMutation.mutate(unit.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No units found. Click "Add Unit" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// GST Rate Section Component
function GSTRateSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
}: {
  data: ItemGSTRate[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ItemGSTRate) => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.gstRates.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gstRates'] });
      toast.success('GST rate deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete GST rate');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">GST Rates</h2>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
          >
            <Plus className="h-4 w-4" />
            Add GST Rate
          </button>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search GST rates..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Rate (%)
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((rate) => (
              <tr key={rate.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {rate.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                  {rate.rate}%
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {rate.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rate.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {rate.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(rate)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this GST rate?')) {
                        deleteMutation.mutate(rate.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No GST rates found. Click "Add GST Rate" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Brand Section Component
function BrandSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: ItemBrand[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ItemBrand) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.brands.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['brands-all'] });
      toast.success('Brand deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete brand');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Item Brands</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Brand
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search brands..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((brand) => (
              <tr key={brand.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {brand.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {brand.code || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {brand.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      brand.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {brand.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(brand)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this brand?')) {
                        deleteMutation.mutate(brand.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No brands found. Click "Add Brand" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Model Section Component
function ModelSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
  brands,
  brandFilter,
  onBrandFilterChange,
}: {
  data: ItemModel[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ItemModel) => void;
  onImport: () => void;
  brands: ItemBrand[];
  brandFilter: string;
  onBrandFilterChange: (brandId: string) => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.models.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      toast.success('Model deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete model');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Item Models</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Model
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search models by name, code..." />
          <SimpleSelect
            value={brandFilter}
            onChange={onBrandFilterChange}
            options={[
              { value: '', label: 'All Brands' },
              ...brands.map((b) => ({ value: b.id, label: b.name })),
            ]}
            placeholder="All Brands"
            aria-label="Filter by brand"
            size="sm"
            wrapperClassName="w-40"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">
                Img
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Brand
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((model) => (
              <tr key={model.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  {model.frontImage ? (
                    <img src={model.frontImage} alt={model.name} className="h-8 w-8 rounded object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                      <Smartphone className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {model.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {model.code || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {model.brand?.name || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {model.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      model.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {model.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(model)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this model?')) {
                        deleteMutation.mutate(model.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No models found. Click "Add Model" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Fault Section Component
function FaultSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
}: {
  data: Fault[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: Fault) => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.faults.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faults'] });
      toast.success('Fault deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete fault');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Faults</h2>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
          >
            <Plus className="h-4 w-4" />
            Add Fault
          </button>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search faults..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Default Price
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Level
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Technician Points
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Warranty
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Tags
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((category) => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {category.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {category.code || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                  {getCurrencySymbol()}{Number(category.defaultPrice).toFixed(2)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    category.level === 1 ? 'bg-green-100 text-green-800' :
                    category.level === 2 ? 'bg-blue-100 text-blue-800' :
                    category.level === 3 ? 'bg-yellow-100 text-yellow-800' :
                    category.level === 4 ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    L{category.level || 1}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {category.technicianPoints} pts
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  {category.warrantyDays > 0 ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {category.warrantyDays}d {category.warrantyDays >= 30 ? `(${(category.warrantyDays / 30).toFixed(category.warrantyDays % 30 === 0 ? 0 : 1)}m)` : ''}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {category.description || '-'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {category.tags ? (
                    <div className="flex flex-wrap gap-1">
                      {category.tags.split(',').map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      category.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(category)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this fault?')) {
                        deleteMutation.mutate(category.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No faults found. Click "Add Fault" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Payment Method Section Component
function PaymentMethodSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: PaymentMethod[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: PaymentMethod) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.paymentMethods.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
      toast.success('Payment method deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete payment method');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Payment Methods</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Payment Method
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search payment methods..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((method) => (
              <tr key={method.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {method.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {method.code}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {method.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      method.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {method.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(method)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this payment method?')) {
                        deleteMutation.mutate(method.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No payment methods found. Click "Add Payment Method" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Expense Category Section Component
function ExpenseCategorySection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: ExpenseCategory[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ExpenseCategory) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.expenseCategories.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseCategories'] });
      toast.success('Expense category deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete expense category');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Expense Categories</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Expense Category
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search expense categories..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((category) => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {category.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {category.code || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {category.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      category.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(category)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this expense category?')) {
                        deleteMutation.mutate(category.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No expense categories found. Click "Add Expense Category" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Damage Condition Section Component
function ServiceIssueSection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: ServiceIssue[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: ServiceIssue) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.serviceIssues.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceIssues'] });
      toast.success('Service issue deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete service issue');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Damage Conditions</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Issue
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search damage conditions..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((issue) => (
              <tr key={issue.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {issue.name}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {issue.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      issue.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {issue.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(issue)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this service issue?')) {
                        deleteMutation.mutate(issue.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No service issues found. Click "Add Issue" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Accessory Section Component
function AccessorySection({
  data,
  isLoading,
  pagination,
  search,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onImport,
}: {
  data: Accessory[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (item: Accessory) => void;
  onImport: () => void;
} & SectionPaginationProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => accessoryApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessories'] });
      toast.success('Accessory deactivated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete accessory');
    },
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-900">Accessories</h2>
          <div className="flex gap-2">
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              onClick={onAdd}
              className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-xs"
            >
              <Plus className="h-4 w-4" />
              Add Accessory
            </button>
          </div>
        </div>
        <SectionSearchBar search={search} onSearchChange={onSearchChange} placeholder="Search accessories..." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((accessory) => (
              <tr key={accessory.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {accessory.name}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                  {accessory.code || '-'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {accessory.description || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      accessory.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {accessory.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    onClick={() => onEdit(accessory)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to deactivate this accessory?')) {
                        deleteMutation.mutate(accessory.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No accessories found. Click "Add Accessory" to create one.
          </div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Model Image Picker (inline helper for the modal)
function ModelImagePicker({
  currentUrl,
  file,
  onFileChange,
  onRemoveExisting,
}: {
  currentUrl?: string | null;
  file: File | null;
  onFileChange: (f: File | null) => void;
  onRemoveExisting?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else if (currentUrl) {
      setPreview(currentUrl);
    } else {
      setPreview(null);
    }
  }, [file, currentUrl]);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={async (e) => {
          const selected = e.target.files?.[0];
          if (selected) {
            const f = await compressImageFile(selected);
            if (f.size > 5 * 1024 * 1024) {
              toast.error('File must be under 5MB');
              return;
            }
            onFileChange(f);
          }
        }}
      />
      {preview ? (
        <div className="relative border rounded-lg overflow-hidden h-28 bg-gray-50 flex items-center justify-center">
          <img src={preview} alt="Preview" className="h-full w-auto object-contain" />
          <button
            type="button"
            onClick={() => {
              if (file) {
                onFileChange(null);
              } else if (onRemoveExisting) {
                onRemoveExisting();
              }
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full h-28 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-purple-400 hover:text-purple-500 transition-colors"
        >
          <Smartphone className="h-6 w-6 mb-1" />
          <span className="text-xs">Upload</span>
        </button>
      )}
    </div>
  );
}

// Modal Component
function MasterDataModal({
  type,
  item,
  onClose,
}: {
  type: MasterType;
  item: any;
  onClose: () => void;
}) {
  const { user } = useAuthStore();
  const canEditWarranty = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  const [formData, setFormData] = useState<any>(
    item || {
      name: '',
      code: '',
      description: '',
      symbol: '',
      rate: 0,
      brandId: undefined,
      defaultPrice: 0,
      level: 1,
      technicianPoints: 0,
      warrantyDays: 0,
      tags: '',
      content: '',
      sortOrder: 0
    }
  );
  const [frontImageFile, setFrontImageFile] = useState<File | null>(null);
  const [backImageFile, setBackImageFile] = useState<File | null>(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const queryClient = useQueryClient();

  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: () => masterDataApi.brands.getAll({ limit: 500, isActive: true }),
    enabled: type === 'model',
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => {
      if (type === 'category') return masterDataApi.categories.create(data);
      if (type === 'unit') return masterDataApi.units.create(data);
      if (type === 'gst-rate') return masterDataApi.gstRates.create(data);
      if (type === 'brand') return masterDataApi.brands.create(data);
      if (type === 'model') return masterDataApi.models.create(data);
      if (type === 'fault') return masterDataApi.faults.create(data);
      if (type === 'payment-method') return masterDataApi.paymentMethods.create(data);
      if (type === 'expense-category') return masterDataApi.expenseCategories.create(data);
      if (type === 'service-issue') return masterDataApi.serviceIssues.create(data);
      if (type === 'accessory') return accessoryApi.create(data);
      if (type === 'invoice-terms') return invoiceTermsApi.create(data);
      if (type === 'estimation-terms') return estimationTermsApi.create(data);
      throw new Error('Invalid type');
    },
    onSuccess: () => {
      const qk = type === 'category' ? 'categories' :
          type === 'unit' ? 'units' :
          type === 'gst-rate' ? 'gstRates' :
          type === 'brand' ? 'brands' :
          type === 'model' ? 'models' :
          type === 'fault' ? 'faults' :
          type === 'payment-method' ? 'paymentMethods' :
          type === 'expense-category' ? 'expenseCategories' :
          type === 'service-issue' ? 'serviceIssues' :
          type === 'invoice-terms' ? 'invoiceTerms' :
          type === 'estimation-terms' ? 'estimationTerms' :
          'accessories';
      queryClient.invalidateQueries({ queryKey: [qk] });
      if (type === 'brand') queryClient.invalidateQueries({ queryKey: ['brands-all'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create item');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => {
      if (type === 'category') return masterDataApi.categories.update(item.id, data);
      if (type === 'unit') return masterDataApi.units.update(item.id, data);
      if (type === 'gst-rate') return masterDataApi.gstRates.update(item.id, data);
      if (type === 'brand') return masterDataApi.brands.update(item.id, data);
      if (type === 'model') return masterDataApi.models.update(item.id, data);
      if (type === 'fault') return masterDataApi.faults.update(item.id, data);
      if (type === 'payment-method') return masterDataApi.paymentMethods.update(item.id, data);
      if (type === 'expense-category') return masterDataApi.expenseCategories.update(item.id, data);
      if (type === 'service-issue') return masterDataApi.serviceIssues.update(item.id, data);
      if (type === 'accessory') return accessoryApi.update(item.id, data);
      if (type === 'invoice-terms') return invoiceTermsApi.update(item.id, data);
      if (type === 'estimation-terms') return estimationTermsApi.update(item.id, data);
      throw new Error('Invalid type');
    },
    onSuccess: () => {
      const qk = type === 'category' ? 'categories' :
          type === 'unit' ? 'units' :
          type === 'gst-rate' ? 'gstRates' :
          type === 'brand' ? 'brands' :
          type === 'model' ? 'models' :
          type === 'fault' ? 'faults' :
          type === 'payment-method' ? 'paymentMethods' :
          type === 'expense-category' ? 'expenseCategories' :
          type === 'service-issue' ? 'serviceIssues' :
          type === 'invoice-terms' ? 'invoiceTerms' :
          type === 'estimation-terms' ? 'estimationTerms' :
          'accessories';
      queryClient.invalidateQueries({ queryKey: [qk] });
      if (type === 'brand') queryClient.invalidateQueries({ queryKey: ['brands-all'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update item');
    },
  });

  const uploadModelImages = async (modelId: string) => {
    if (!frontImageFile && !backImageFile) return;
    setIsUploadingImages(true);
    try {
      const fd = new FormData();
      if (frontImageFile) fd.append('frontImage', frontImageFile);
      if (backImageFile) fd.append('backImage', backImageFile);
      await modelApi.uploadImages(modelId, fd);
    } catch {
      toast.error('Model saved but image upload failed');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (item) {
      updateMutation.mutate(formData, {
        onSuccess: async (data: any) => {
          if (type === 'model' && (frontImageFile || backImageFile)) {
            await uploadModelImages(data?.id || item.id);
            queryClient.invalidateQueries({ queryKey: ['models'] });
          }
        },
      });
    } else {
      createMutation.mutate(formData, {
        onSuccess: async (data: any) => {
          if (type === 'model' && (frontImageFile || backImageFile)) {
            await uploadModelImages(data?.id);
            queryClient.invalidateQueries({ queryKey: ['models'] });
          }
        },
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className={`bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full ${type === 'model' ? 'sm:max-w-lg' : 'sm:max-w-md'} max-h-[85vh] sm:max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            {item ? 'Edit' : 'Add'}{' '}
            {type === 'category' ? 'Category' :
             type === 'unit' ? 'Unit' :
             type === 'gst-rate' ? 'GST Rate' :
             type === 'brand' ? 'Brand' :
             type === 'model' ? 'Model' :
             type === 'fault' ? 'Fault' :
             type === 'payment-method' ? 'Payment Method' :
             type === 'expense-category' ? 'Expense Category' :
             type === 'service-issue' ? 'Damage Condition' :
             type === 'invoice-terms' ? 'Invoice Term' :
             type === 'estimation-terms' ? 'Estimation Term' :
             'Accessory'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          {/* Content field for Invoice/Estimation Terms */}
          {(type === 'invoice-terms' || type === 'estimation-terms') ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Term Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={3}
                  maxLength={500}
                  required
                  placeholder="Enter the term or condition text..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.content?.length || 0}/500 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sortOrder || ''}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  min={0}
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>
            </>
          ) : (
            /* Name field for other types */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          )}
          {(type === 'category' || type === 'unit' || type === 'brand' || type === 'model' || type === 'fault' || type === 'payment-method' || type === 'expense-category' || type === 'accessory') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code {type === 'payment-method' || type === 'unit' ? '*' : ''}
              </label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required={type === 'payment-method' || type === 'unit'}
              />
            </div>
          )}
          {type === 'unit' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symbol
              </label>
              <input
                type="text"
                value={formData.symbol || ''}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
          {type === 'model' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand (Optional)
              </label>
              <SimpleSelect
                value={formData.brandId || ''}
                onChange={(v) => setFormData({ ...formData, brandId: v || undefined })}
                options={[
                  { value: '', label: 'Select Brand' },
                  ...(brandsQuery.data?.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
                ]}
                placeholder="Select Brand"
                aria-label="Brand"
                wrapperClassName="w-full min-w-0"
                className="focus:!ring-purple-500 focus:!border-purple-500"
              />
            </div>
          )}
          {type === 'model' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Front Image</label>
                <ModelImagePicker
                  currentUrl={formData.frontImage}
                  file={frontImageFile}
                  onFileChange={setFrontImageFile}
                  onRemoveExisting={item ? async () => {
                    try {
                      await modelApi.deleteImage(item.id, 'front');
                      setFormData({ ...formData, frontImage: null });
                      queryClient.invalidateQueries({ queryKey: ['models'] });
                      toast.success('Front image removed');
                    } catch {
                      toast.error('Failed to remove image');
                    }
                  } : undefined}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Back Image</label>
                <ModelImagePicker
                  currentUrl={formData.backImage}
                  file={backImageFile}
                  onFileChange={setBackImageFile}
                  onRemoveExisting={item ? async () => {
                    try {
                      await modelApi.deleteImage(item.id, 'back');
                      setFormData({ ...formData, backImage: null });
                      queryClient.invalidateQueries({ queryKey: ['models'] });
                      toast.success('Back image removed');
                    } catch {
                      toast.error('Failed to remove image');
                    }
                  } : undefined}
                />
              </div>
            </div>
          )}
          {type === 'gst-rate' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate (%) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.rate || ''}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          )}
          {type === 'fault' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.defaultPrice || ''}
                  onChange={(e) => setFormData({ ...formData, defaultPrice: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level *
                </label>
                <SimpleSelect
                  value={String(formData.level || 1)}
                  onChange={(v) => setFormData({ ...formData, level: parseInt(v) })}
                  options={FAULT_LEVEL_OPTIONS}
                  aria-label="Fault level"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Technician Points *
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.technicianPoints || ''}
                  onChange={(e) => setFormData({ ...formData, technicianPoints: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={formData.tags || ''}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., mic, speaker, board (comma separated)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">Enter comma-separated tags for parts categorization</p>
              </div>
              {canEditWarranty && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-purple-600" />
                      Warranty Period (Days)
                    </div>
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.warrantyDays || ''}
                    onChange={(e) => setFormData({ ...formData, warrantyDays: parseInt(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0 = No warranty"
                  />
                  {(formData.warrantyDays || 0) > 0 && (
                    <p className="mt-1 text-xs text-purple-600 font-medium">
                      = {formData.warrantyDays >= 30
                        ? `${(formData.warrantyDays / 30).toFixed(formData.warrantyDays % 30 === 0 ? 0 : 1)} month(s)`
                        : `${formData.warrantyDays} day(s)`}
                      {' '}service warranty
                    </p>
                  )}
                  {(formData.warrantyDays || 0) === 0 && (
                    <p className="mt-1 text-xs text-gray-500">Set to 0 for no warranty. Only Admin can change this.</p>
                  )}
                </div>
              )}
              {!canEditWarranty && (formData.warrantyDays || 0) > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-700">
                    <Shield className="h-3.5 w-3.5 inline mr-1" />
                    Warranty: {formData.warrantyDays} days
                    {formData.warrantyDays >= 30 ? ` (${(formData.warrantyDays / 30).toFixed(formData.warrantyDays % 30 === 0 ? 0 : 1)} months)` : ''}
                    <span className="text-purple-500 ml-1">(Only Admin can modify)</span>
                  </p>
                </div>
              )}
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending || isUploadingImages}
              className="w-full sm:flex-1 px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {(createMutation.isPending || updateMutation.isPending || isUploadingImages) ? (
                isUploadingImages ? 'Uploading images...' : 'Saving...'
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  {item ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Import Modal Component
function ImportModal({
  type,
  onClose,
  onSuccess,
}: {
  type: MasterType;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);
  const [error, setError] = useState<string>('');

  // Fetch brands for model import (name -> id resolution)
  const brandsForImport = useQuery({
    queryKey: ['brands'],
    queryFn: () => masterDataApi.brands.getAll({ limit: 500, isActive: true }),
    staleTime: 10 * 60 * 1000,
    enabled: type === 'model',
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setError('Please select a CSV or Excel file');
        return;
      }
      setFile(selectedFile);
      setError('');

      if (fileName.endsWith('.csv')) {
        parseCSV(selectedFile);
      } else {
        parseExcel(selectedFile);
      }
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        setError('CSV file is empty or invalid');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const data = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim());
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        return obj;
      });

      setPreview(data);
    };
    reader.readAsText(file);
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (jsonData.length < 2) {
          setError('Excel file is empty or invalid');
          return;
        }

        const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
        const rows = jsonData.slice(1, 6).map((row: any) => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = String(row[index] || '').trim();
          });
          return obj;
        });

        setPreview(rows);
      } catch (err) {
        setError('Failed to parse Excel file');
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError('');

    try {
      let data: any[] = [];
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.csv')) {
        // Parse CSV
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim());
        data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = values[index] || '';
          });
          return obj;
        });
      } else {
        // Parse Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbookData = new Uint8Array(arrayBuffer);
        const workbook = XLSX.read(workbookData, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
        data = jsonData.slice(1).map((row: any) => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = String(row[index] || '').trim();
          });
          return obj;
        }).filter(row => Object.values(row).some(v => v)); // Filter empty rows
      }

        // Import each row
        let successCount = 0;
        let failCount = 0;
        const failedRows: { row: string; error: string }[] = [];

        for (const row of data) {
          try {
            if (type === 'category') {
              await masterDataApi.categories.create(row);
            } else if (type === 'unit') {
              await masterDataApi.units.create(row);
            } else if (type === 'gst-rate') {
              await masterDataApi.gstRates.create({ ...row, rate: parseFloat(row.rate) });
            } else if (type === 'brand') {
              await masterDataApi.brands.create(row);
            } else if (type === 'model') {
              const brandName = (row.brandName || row.Brand || row.brand || '').trim();
              await masterDataApi.models.create({
                name: row.name,
                code: row.code,
                description: row.description,
                ...(brandName ? { brandName } : {}),
              });
            } else if (type === 'fault') {
              await masterDataApi.faults.create({
                ...row,
                defaultPrice: parseFloat(row.defaultPrice || 0),
                level: parseInt(row.level || 1),
                technicianPoints: parseInt(row.technicianPoints || 0),
              });
            } else if (type === 'payment-method') {
              await masterDataApi.paymentMethods.create(row);
            } else if (type === 'expense-category') {
              await masterDataApi.expenseCategories.create(row);
            } else if (type === 'service-issue') {
              await masterDataApi.serviceIssues.create(row);
            }
            successCount++;
          } catch (err: any) {
            failCount++;
            const msg = err?.response?.data?.message || 'Unknown error';
            failedRows.push({ row: row.name || row.code || `Row ${i + 1}`, error: msg });
          }
        }

      if (failCount === 0) {
        toast.success(`Import completed! ${successCount} items imported successfully.`);
      } else if (successCount === 0) {
        toast.error(`Import failed. All ${failCount} items failed.`, {
          description: failedRows.slice(0, 3).map(r => `${r.row}: ${r.error}`).join('\n'),
          duration: 8000,
        });
      } else {
        toast.warning(`Import completed. ${successCount} successful, ${failCount} failed.`, {
          description: failedRows.slice(0, 3).map(r => `${r.row}: ${r.error}`).join('\n'),
          duration: 8000,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    } finally {
      setIsProcessing(false);
    }
  };

  const getCSVTemplate = () => {
    const templates: Record<MasterType, string> = {
      'category': 'name,code,description\nElectronics,ELEC,Electronic items\nMobile Accessories,MOB_ACC,Mobile accessories',
      'unit': 'name,code,symbol,description\nPiece,PC,Pcs,Individual pieces\nBox,BOX,Box,Packed in boxes',
      'gst-rate': 'name,rate,description\nGST 5%,5,5% GST rate\nGST 18%,18,18% GST rate',
      'brand': 'name,code,description\nSamsung,SAM,Samsung Electronics\nApple,APP,Apple Inc.',
      'model': 'name,code,brandName,description\niPhone 15,IP15,Apple,Latest iPhone model\nGalaxy S24,GS24,Samsung,Latest Samsung Galaxy',
      'fault': 'name,code,defaultPrice,level,technicianPoints,description\nScreen Replacement,SCR_REP,1500,3,10,Screen replacement service\nBattery Replacement,BAT_REP,800,2,5,Battery replacement service',
      'payment-method': 'name,code,description\nCash,CASH,Cash payment\nUPI,UPI,UPI payment\nCard,CARD,Card payment',
      'expense-category': 'name,code,description\nRent,RENT,Office rent\nUtilities,UTIL,Electricity and water\nSalaries,SAL,Employee salaries',
      'service-issue': 'name,description\nScreen Cracked,Display glass or screen is cracked\nBattery Draining Fast,Battery discharges quickly\nNot Charging,Device not charging when connected',
    };
    return templates[type];
  };

  const downloadTemplate = () => {
    const template = getCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = () => {
    const templates: Record<MasterType, { headers: string[], samples: any[][] }> = {
      'category': {
        headers: ['name', 'code', 'description'],
        samples: [
          ['Electronics', 'ELEC', 'Electronic items'],
          ['Mobile Accessories', 'MOB_ACC', 'Mobile accessories'],
          ['Spare Parts', 'SPARE', 'Spare parts for repair'],
        ],
      },
      'unit': {
        headers: ['name', 'code', 'symbol', 'description'],
        samples: [
          ['Piece', 'PC', 'Pcs', 'Individual pieces'],
          ['Box', 'BOX', 'Box', 'Packed in boxes'],
          ['Pack', 'PACK', 'Pk', 'Packaged items'],
        ],
      },
      'gst-rate': {
        headers: ['name', 'rate', 'description'],
        samples: [
          ['GST 5%', '5', '5% GST rate'],
          ['GST 12%', '12', '12% GST rate'],
          ['GST 18%', '18', '18% GST rate'],
        ],
      },
      'brand': {
        headers: ['name', 'code', 'description'],
        samples: [
          ['Samsung', 'SAM', 'Samsung Electronics'],
          ['Apple', 'APP', 'Apple Inc.'],
          ['OnePlus', 'OPL', 'OnePlus Technology'],
        ],
      },
      'model': {
        headers: ['name', 'code', 'Brand', 'description'],
        samples: [
          ['iPhone 15', 'IP15', 'Apple', 'Latest iPhone model'],
          ['Galaxy S24', 'GS24', 'Samsung', 'Latest Samsung Galaxy'],
          ['OnePlus 12', 'OP12', 'OnePlus', 'Latest OnePlus flagship'],
        ],
      },
      'fault': {
        headers: ['name', 'code', 'defaultPrice', 'level', 'technicianPoints', 'description'],
        samples: [
          ['Screen Replacement', 'SCR_REP', '1500', '3', '10', 'Screen replacement service'],
          ['Battery Replacement', 'BAT_REP', '800', '2', '5', 'Battery replacement service'],
          ['Software Update', 'SW_UPD', '300', '1', '2', 'Software update service'],
        ],
      },
      'payment-method': {
        headers: ['name', 'code', 'description'],
        samples: [
          ['Cash', 'CASH', 'Cash payment'],
          ['UPI', 'UPI', 'UPI payment'],
          ['Card', 'CARD', 'Card payment'],
        ],
      },
      'expense-category': {
        headers: ['name', 'code', 'description'],
        samples: [
          ['Rent', 'RENT', 'Office rent expense'],
          ['Utilities', 'UTIL', 'Electricity and water bills'],
          ['Salaries', 'SAL', 'Employee salary payments'],
        ],
      },
      'service-issue': {
        headers: ['name', 'description'],
        samples: [
          ['Screen Cracked', 'Display glass or screen is cracked or shattered'],
          ['Battery Draining Fast', 'Battery discharges quickly, poor battery life'],
          ['Not Charging', 'Device not charging when connected to charger'],
        ],
      },
    };

    const template = templates[type];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([template.headers, ...template.samples]);

    // Set column widths
    const colWidths = template.headers.map((h, i) => {
      if (h === 'description') return { wch: 40 };
      if (h === 'name') return { wch: 25 };
      return { wch: 15 };
    });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${type}-template-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-gray-900">
            Import {type === 'category' ? 'Categories' :
             type === 'unit' ? 'Units' :
             type === 'gst-rate' ? 'GST Rates' :
             type === 'brand' ? 'Brands' :
             type === 'model' ? 'Models' :
             type === 'fault' ? 'Faults' :
             type === 'payment-method' ? 'Payment Methods' :
             type === 'expense-category' ? 'Expense Categories' :
             type === 'service-issue' ? 'Damage Conditions' :
             'Accessories'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Download className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">Download Template</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Download the template file with the correct format and fill it with your data
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={downloadExcelTemplate}
                    className="text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Excel
                  </button>
                  <button
                    onClick={downloadTemplate}
                    className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {type === 'model' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The "Brand" column should match existing brand names exactly. Models with unrecognized brand names will be created without a brand association.
              </p>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File (CSV or Excel)
            </label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {file.name}
              </p>
            )}
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Preview (first 5 rows)</h4>
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(preview[0]).map((key) => (
                        <th key={key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {preview.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value: any, i) => (
                          <td key={i} className="px-4 py-2 whitespace-nowrap text-gray-900">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || isProcessing}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
